import React, { useState } from 'react';
import { FaArrowLeft, FaPhone, FaHardHat, FaImages, FaShieldAlt, FaLock, FaEye, FaUserShield, FaUsers, FaPaintRoller, FaTools, FaWrench, FaBars, FaTimes, FaWindowMaximize, FaHome } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Footer from '../layout/Footer';
import ReusableDropdown from '../ui/ReusableDropdown.jsx';
import './PrivacyPolicy.css';
import '../layout/PageMobileNav.css';

const PrivacyPolicyPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = 'tel:+13136316020';
  };

  // Mobile menu handlers
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Navigation services for dropdown
  const navigationServices = [
    { id: 'roofing', text: 'Roofing', to: '/roofing', icon: FaHome },
    { id: 'windows', text: 'Windows', to: '/windows', icon: FaWindowMaximize },
    { id: 'siding', text: 'Siding', to: '/siding', icon: FaPaintRoller },
    { id: 'gutters', text: 'Gutters', to: '/gutters', icon: FaTools },
    { id: 'repairs', text: 'Repairs', to: '/repairs', icon: FaWrench },
    { id: 'all-services', text: 'All Services', to: '/all-services', icon: FaHardHat }
  ];

  return (
    <div className="privacy-policy-page">
      <Helmet>
        <title>RC Roofing - Privacy Policy</title>
        <meta name="description" content="RC Roofing Privacy Policy - Learn how we collect, use, and protect your personal information." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="page-header">
        <div className="container">
          <div className="page-nav">
            <Link to="/" className="back-btn">
              <FaArrowLeft className="back-icon" />
              Back to Home
            </Link>
            {/* Desktop Navigation */}
            <div className="page-nav-right desktop-nav">
              <button 
                className="nav-item" 
                onClick={handleContactClick} 
                title="Call us"
              >
                <FaPhone className="nav-icon" />
                Call us
              </button>
              
              <ReusableDropdown
                triggerText="Services"
                items={navigationServices}
                className="privacy-services-dropdown"
                triggerClassName="privacy-nav-item"
                iconComponent={FaHardHat}
                showIcons={true}
              />
              
              <Link 
                to="/roof-insurance" 
                className="nav-item" 
                title="Roof Insurance"
              >
                <FaShieldAlt className="nav-icon" />
                Insurance
              </Link>
              <Link 
                to="/gallery" 
                className="nav-item" 
                title="Our Gallery"
              >
                <FaImages className="nav-icon" />
                Our Gallery
              </Link>
            </div>

            {/* Mobile Navigation */}
            <div className="mobile-nav">
              <button 
                className="hamburger-btn"
                onClick={toggleMobileMenu}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="page-mobile-menu">
          <div className="page-mobile-menu-header">
            <div className="page-mobile-menu-title">Navigation</div>
            <button 
              className="page-mobile-close-btn"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
          </div>
          
          <div className="page-mobile-menu-content">
            <button 
              className="page-mobile-nav-item"
              onClick={(e) => {
                handleContactClick(e);
                closeMobileMenu();
              }}
            >
              <FaPhone className="nav-icon" />
              Call us
            </button>
            
            <ReusableDropdown
              triggerText="Services"
              items={navigationServices}
              className="page-mobile-dropdown mobile-nav"
              iconComponent={FaHardHat}
              showIcons={true}
              onItemClick={closeMobileMenu}
            />
            
            <Link 
              to="/roof-insurance"
              className="page-mobile-nav-item"
              onClick={closeMobileMenu}
            >
              <FaShieldAlt className="nav-icon" />
              Insurance
            </Link>
            
            <Link 
              to="/gallery"
              className="page-mobile-nav-item"
              onClick={closeMobileMenu}
            >
              <FaImages className="nav-icon" />
              Our Gallery
            </Link>
          </div>
        </div>
      )}
      
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Privacy Policy</h1>
          <p className="page-subtitle">
            Your privacy is important to us. Learn how we protect and handle your information.
          </p>
        </div>
      </div>

      <div className="privacy-content">
        <div className="container">
          <div className="privacy-intro">
            <div className="privacy-icon">
              <FaShieldAlt />
            </div>
            <p className="last-updated">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          <div className="privacy-sections">
            <section className="privacy-section">
              <h2><FaLock className="section-icon" />Information We Collect</h2>
              <div className="section-content">
                <h3>Personal Information</h3>
                <p>We may collect personal information that you provide directly to us, including:</p>
                <ul>
                  <li>Name and contact information (email address, phone number, mailing address)</li>
                  <li>Project details and requirements</li>
                  <li>Property information and specifications</li>
                  <li>Payment information (processed securely through third-party providers)</li>
                  <li>Communications and correspondence with our team</li>
                </ul>

                <h3>Automatically Collected Information</h3>
                <p>We may automatically collect certain information when you visit our website:</p>
                <ul>
                  <li>IP address and browser information</li>
                  <li>Pages visited and time spent on our site</li>
                  <li>Referring website information</li>
                  <li>Device and operating system information</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2><FaEye className="section-icon" />How We Use Your Information</h2>
              <div className="section-content">
                <p>We use the information we collect to:</p>
                <ul>
                  <li>Provide and improve our remodeling services</li>
                  <li>Respond to your inquiries and provide customer support</li>
                  <li>Process transactions and manage your account</li>
                  <li>Send you important updates about your project</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                  <li>Improve our website and services</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2><FaUserShield className="section-icon" />Information Sharing and Disclosure</h2>
              <div className="section-content">
                <p>We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:</p>
                <ul>
                  <li><strong>Service Providers:</strong> We may share information with trusted third parties who assist us in operating our website and conducting our business</li>
                  <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights and safety</li>
                  <li><strong>Business Transfers:</strong> In the event of a merger or acquisition, your information may be transferred to the new entity</li>
                  <li><strong>Consent:</strong> We may share information with your explicit consent</li>
                </ul>
                <p><strong>Note:</strong> All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>
              </div>
            </section>

            <section className="privacy-section">
              <h2><FaLock className="section-icon" />Data Security</h2>
              <div className="section-content">
                <p>We implement appropriate security measures to protect your personal information:</p>
                <ul>
                  <li>SSL encryption for data transmission</li>
                  <li>Secure servers and databases</li>
                  <li>Regular security assessments and updates</li>
                  <li>Limited access to personal information by authorized personnel only</li>
                  <li>Secure payment processing through certified providers</li>
                </ul>
                <p className="security-note">
                  <strong>Note:</strong> While we strive to protect your information, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security but will notify you of any security breaches as required by law.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Your Rights and Choices</h2>
              <div className="section-content">
                <p>You have certain rights regarding your personal information:</p>
                <ul>
                  <li><strong>Access:</strong> Request access to your personal information we hold</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal requirements)</li>
                  <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
                  <li><strong>Opt-out:</strong> Opt out of marketing communications at any time</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Cookies and Tracking Technologies</h2>
              <div className="section-content">
                <p>Our website may use cookies and similar tracking technologies to:</p>
                <ul>
                  <li>Improve website functionality and user experience</li>
                  <li>Analyze website traffic and usage patterns</li>
                  <li>Remember your preferences and settings</li>
                  <li>Provide personalized content and advertisements</li>
                </ul>
                <p>You can control cookie settings through your browser preferences. However, disabling cookies may affect website functionality.</p>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Third-Party Links</h2>
              <div className="section-content">
                <p>Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.</p>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Children's Privacy</h2>
              <div className="section-content">
                <p>Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information.</p>
              </div>
            </section>

            <section className="privacy-section">
              <h2>Changes to This Privacy Policy</h2>
              <div className="section-content">
                <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
                <ul>
                  <li>Posting the updated policy on our website</li>
                  <li>Sending you an email notification (if applicable)</li>
                  <li>Updating the "Last updated" date at the top of this policy</li>
                </ul>
                <p>Your continued use of our services after any changes constitutes acceptance of the updated policy.</p>
              </div>
            </section>

            <section className="privacy-section contact-section">
              <h2>Contact Us</h2>
              <div className="section-content">
                <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us:</p>
                <div className="contact-info">
                  <div className="contact-item">
                    <strong>RC Roofing</strong><br />
                    Phone: <a href="tel:+13136316020">(313) 631-6020</a><br />
                    Email: <a href="mailto:hi@rcroof.co">hi@rcroof.co</a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
